import { useMemo, useEffect } from "react";
import { Row, Col, Progress } from "antd";
import AltButton from "../../components/buttons/AltButton";
import Statuscard from "./components/StatusCard";
import { useNavigate } from "react-router-dom";
import useBasicInfo from "../../utils/use-basic-info";
import useStepper from "../../utils/use-stepper";
import useWillInfo from "../../utils/use-will-info";
import moment from "moment";
import { useSelector } from "react-redux";

const Dashboard = () => {
  const navigate = useNavigate();
  const basic = useBasicInfo();
  const will = useWillInfo();
  const { nextPath, isFullyComplete } = useStepper();

  // Redirect to Partner Estate page as first step if user has partner
  useEffect(() => {
    const hasPartner = will?.family?.hasPartner;
    const planForPartnerStatus = will?.family?.planForPartner?.status;
    
    // If user has partner and hasn't decided on partner estate plan, show that page first
    if (hasPartner && planForPartnerStatus === false) {
      navigate("/setup/partner-estate", { replace: true });
    }
  }, [will?.family?.hasPartner, will?.family?.planForPartner?.status, navigate]);

  const progressPercent = useSelector(
    (state) => state.metadata?.percentage ?? 0,
  );

  const classification = useSelector(
    (state) => state.metadata?.classification ?? [],
  );

  const sectionStatus = useSelector(
    (state) => state.metadata?.sectionStatus ?? {},
  );

  const hasData = (key) => {
    switch (key) {
      case "family":
        return (
          !!will.family?.partnerFirstName ||
          !!will.family?.children?.length ||
          !!will.family?.pets?.length
        );
      case "guardian":
        return (
          !!will.petGuardian?.fullname || !!will.alternatePetGuardian?.fullname
        );
      case "poaAttorney":
        return (
          !!will.poaAttorney?.putPartnerInCharge ||
          !!will.poaAttorney?.executor?.fullname
        );
      case "poaCare":
        return (
          !!will.poaCare?.putPartnerInCharge ||
          !!will.poaCare?.executor?.fullname
        );
      default:
        return false;
    }
  };

  const shouldShow = (key) => sectionStatus[key]?.isRequired || hasData(key);

  const [hour, am_pm] = moment().format("h,A").split(",");
  const timeOfDay = useMemo(() => {
    if (am_pm === "AM") {
      return `morning`;
    } else {
      if (hour === 12 || hour < 6) {
        return `afternoon`;
      } else {
        return `evening`;
      }
    }
  }, [hour, am_pm]);

  return (
    <div className="xl:max-w-[72rem] w-full mx-auto">
      <Row gutter={24}>
        <Col
          md={12}
          xs={24}
          className="mb-10 lg:mb-0"
        >
          <div className="flex flex-col items-center py-5 mb-5 text-center lg:mb-10">
            <p className="mb-1 text-xl font-bold app-text">
              Good {timeOfDay}, {basic?.firstName}
            </p>
            <div>
              {isFullyComplete ? (
                <p className="px-5 text-lg font-light app-text">
                  You're all done! Look over your information again, then
                  generate your documents.
                </p>
              ) : (
                <p className="px-5 text-lg font-light app-text">
                  You're halfway done! Please continue the questionnaire to
                  generate your documents.
                </p>
              )}
            </div>

            <div className="w-full my-10">
              <div className="flex items-center justify-between">
                <p className="text-sm font-light app-text">Progress</p>
                <p className="text-sm font-light app-text">
                  {progressPercent}%
                </p>
              </div>
              <Progress
                percent={progressPercent}
                showInfo={false}
                strokeColor="#5A741D"
                strokeWidth={12}
              />
            </div>
            <div>
              {isFullyComplete ? (
                <AltButton
                  className="h-10"
                  onClick={() => navigate("/setup/complete")}
                >
                  Generate Documents
                </AltButton>
              ) : (
                <AltButton
                  className="h-10"
                  onClick={() => navigate(nextPath)}
                >
                  Continue Questionnaire
                </AltButton>
              )}
            </div>
          </div>
          <Row gutter={16}>
            <Col xs={12}>
              <div
                className="p-2 text-center bg-[#F2FAD6] border border-[#F2FAD6] rounded-md md:h-48 h-36 flex flex-col items-center justify-center cursor-pointer"
                onClick={() => navigate("/documents")}
              >
                <p className="text-base font-bold app-text">My Documents</p>
                <p className="text-sm font-light app-text">
                  Where the important stuff lives.
                </p>
              </div>
            </Col>
            <Col xs={12}>
              <div
                className="p-2 text-center bg-[#F2FAD6] border border-[#F2FAD6] rounded-md md:h-48 h-36 flex flex-col items-center justify-center cursor-pointer"
                onClick={() => navigate("/summary")}
              >
                <p className="text-base font-bold app-text">My Summary</p>
                <p className="text-sm font-light app-text ">
                  Review all of your answers here.
                </p>
              </div>
            </Col>
          </Row>
        </Col>

        <Col
          md={12}
          xs={24}
        >
          {shouldShow("basics") && (
            <>
              <Statuscard
                title="The Basics"
                description="Basic information about you."
                path="/basic/complete"
                status={
                  classification.includes("The Basics")
                    ? "Incomplete"
                    : "Complete"
                }
              />
              <br />
            </>
          )}
          {shouldShow("family") && (
            <>
              <Statuscard
                title="Family"
                description="Information about your family."
                path="/family/complete"
                status={
                  classification.includes("Family") ? "Incomplete" : "Complete"
                }
              />
              <br />
            </>
          )}
          {shouldShow("guardian") && (
            <>
              <Statuscard
                title="Guardians"
                description="Appointing someone to care for your minor children."
                path="/guardians/complete"
                status={
                  classification.includes("Guardians")
                    ? "Incomplete"
                    : "Complete"
                }
              />
              <br />
            </>
          )}
          {shouldShow("distribution") && (
            <>
              <Statuscard
                title="Distribution"
                description="Who gets what?"
                path="/distribution/complete"
                status={
                  classification.includes("Distribution")
                    ? "Incomplete"
                    : "Complete"
                }
              />
              <br />
            </>
          )}
          {shouldShow("executor") && (
            <>
              <Statuscard
                title="Executor"
                description="The person who will manage your affairs and carry out your wishes."
                path="/executor/complete"
                status={
                  classification.includes("Executor")
                    ? "Incomplete"
                    : "Complete"
                }
              />
              <br />
            </>
          )}
          {shouldShow("poaAttorney") && (
            <>
              <Statuscard
                title="POA for Property"
                description="Appointing a tusted person to handle your finances, if you become incapable."
                path="/poa/complete"
                status={
                  classification.includes("POA for Property")
                    ? "Incomplete"
                    : "Complete"
                }
              />
              <br />
            </>
          )}
          {shouldShow("poaCare") && (
            <>
              <Statuscard
                title="POA for Care"
                description="Appointing a tusted person to make decisions about your health care, if you become incapable."
                path="/poa/care/complete"
                status={
                  classification.includes("POA for Care")
                    ? "Incomplete"
                    : "Complete"
                }
              />
              <br />
            </>
          )}
          {isFullyComplete ? (
            <>
              <div className="mt-7">
                <AltButton
                  className="h-10 mx-auto"
                  onClick={() => navigate("/setup/complete")}
                >
                  Generate Documents
                </AltButton>
              </div>
            </>
          ) : null}
        </Col>
      </Row>
    </div>
  );
};

export default Dashboard;
